/* ============================================================
   THE HEADER STOPS RELOADING

   Devin, 10 Aug: "when i click on a product on the home page it reloads the
   whole header and takes me to the product display page. can we do the same
   trick we did with the search results to the whole website so the header
   doesn't keep loading all the time?"

   Same trick, generalised: fetch the destination, lift its #MainContent out,
   put it where the current one was. The masthead, the nav, the cart drawer and
   the footer are never re-parsed, so they never flash — and the wordmark, the
   fonts and the polka dots are not re-decoded on every click.

   WHAT MAKES THIS SAFE RATHER THAN CLEVER is knowing what NOT to take over.
   The list in isSwappable() is the whole design: anything that leaves the shop,
   anything the browser or Shopify handles better than we do (checkout, account,
   file downloads, new tabs, modified clicks), and anything a merchant has
   marked. Everything else is an ordinary page of this theme, which means it has
   a #MainContent and nothing outside it that needed to change.

   THE THINGS A REAL NAVIGATION DOES BESIDES SWAPPING MARKUP, all of which have
   to be done by hand here:

     - history, forward and back, including the scroll position on the way back
     - <title>, and the template attribute that CSS keys off
     - re-running the scripts that came down with the new markup
     - moving focus, so a screen reader is told the page changed
     - telling Shopify's analytics that a page was viewed

   IT DEGRADES TO A NORMAL LINK. Every failure path — a non-HTML response, a
   fetch error, a page with no #MainContent — falls through to
   location.href, which is exactly what would have happened without this file.
   ============================================================ */
(function () {
  'use strict';

  var MAIN_ID = 'MainContent';

  /* Paths the browser, or Shopify, must own. /checkout and /account run on
     Shopify's own pages; /cart/change and friends are endpoints, not documents;
     /tools and /apps belong to whatever app is mounted there. */
  var NEVER = /^\/(checkout|account|tools|apps|services|password|challenge|cart\/(add|change|update|clear))(\/|$|\?)/;
  var FILE = /\.(pdf|jpe?g|png|gif|svg|webp|zip|csv|txt|xml|json|mp4|mov)(\?|$)/i;

  var inFlight = null;
  var swapping = false;

  /* What is actually rendered right now. Compared on popstate, because the flag
     we put in history state is not enough on its own — see the note there. */
  var currentPath = location.pathname;

  /* WHERE EACH PAGE WAS LEFT, KEYED BY PATH. Two things ruled out the obvious
     alternatives. history.state is not enough: Horizon's own components push
     entries as you page a collection, and those carry no scroll of ours, so
     Back onto one restored to the top. Keying by full URL is not enough either,
     for the same reason — the collection's URL had become `?page=1` by the time
     the shopper left it, so the scroll saved under the bare path was never
     found again. Verified: Back from a product landed at 0 rather than at the
     900 it was left at. The path is the thing that stays put. */
  var scrollByPath = Object.create(null);

  /* THE PAGE DOES NOT SCROLL ON THE WINDOW. Horizon puts `overflow: hidden` on
     html and body and scrolls `.page-wrapper` instead, so window.scrollY is
     always 0 and window.scrollTo does nothing. Measured on a collection:
     document height 1000 (the viewport) against the wrapper's 3568.

     Everything about scrolling here — remembering a position, returning to the
     top after a swap, restoring on Back — has to go through whichever element
     is actually scrolling, or it silently does nothing at all. That was the
     failure mode: swaps left the new page at the old page's scroll position,
     and no test caught it because window.scrollY read 0 either way. */
  function scroller() {
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      var overflow = getComputedStyle(wrapper).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && wrapper.scrollHeight > wrapper.clientHeight) {
        return wrapper;
      }
    }
    return document.scrollingElement || document.documentElement;
  }

  function scrollPosition() {
    var el = scroller();
    return el === document.scrollingElement || el === document.documentElement ? window.scrollY : el.scrollTop;
  }

  /* INSTANTLY, AND THAT TAKES SAYING SO TWICE. The theme sets
     `scroll-behavior: smooth` on .page-wrapper and on html, which turns an
     assignment to scrollTop into an ANIMATION: after a swap the new page slid
     up from wherever the old one had been left, over about half a second
     (Devin, 10 Aug: "when i click on a product i get a forced scroll to the
     top"). Sampled it — 1200, 1168, 1100, 948, 737, ... 0.

     A real navigation arrives at the top; it does not travel there. `behavior:
     'instant'` says that where it is understood, and the inline override is for
     browsers that ignore the option — set, jump, put back on the next frame so
     the shopper's own smooth scrolling is untouched. */
  function scrollToY(y) {
    var el = scroller();
    var target = el === document.scrollingElement || el === document.documentElement ? null : el;
    var styled = target || document.documentElement;
    var previous = styled.style.scrollBehavior;
    styled.style.scrollBehavior = 'auto';

    if (target) {
      try {
        target.scrollTo({ top: y, behavior: 'instant' });
      } catch (e) {
        target.scrollTop = y;
      }
    } else {
      try {
        window.scrollTo({ top: y, behavior: 'instant' });
      } catch (e) {
        window.scrollTo(0, y);
      }
    }

    window.requestAnimationFrame(function () {
      styled.style.scrollBehavior = previous;
    });
  }

  function canSwap() {
    return !!(
      document.getElementById(MAIN_ID) &&
      window.fetch &&
      window.history &&
      window.history.pushState &&
      window.AbortController &&
      window.DOMParser
    );
  }

  function isSwappable(link, event) {
    if (!link || !link.href) return false;
    if (event) {
      /* A modified click means the shopper asked the BROWSER for something —
         a new tab, a download, a saved link. Never intercept those. */
      if (event.defaultPrevented) return false;
      if (event.button != null && event.button !== 0) return false;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    }
    if (link.target && link.target !== '' && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.dataset.noSwap != null) return false;
    if (link.getAttribute('href').charAt(0) === '#') return false;
    if (link.origin !== window.location.origin) return false;
    if (NEVER.test(link.pathname)) return false;
    if (FILE.test(link.pathname)) return false;
    /* Same page, different hash: let the browser scroll. */
    if (link.pathname === location.pathname && link.search === location.search && link.hash) return false;
    return true;
  }

  /* innerHTML and DOMParser both produce inert <script> elements, so any script
     that came down with the markup has to be recreated to run. Re-adding a
     module already in the registry is a no-op, which is what makes this safe to
     do on every swap. */
  function activateScripts(root) {
    var scripts = root.querySelectorAll('script');
    Array.prototype.forEach.call(scripts, function (original) {
      /* ALREADY LOADED, LEAVE IT. A src that is on the page already has run;
         re-adding it re-fetches a file for no gain, and for a cross-origin one
         WebKit refuses the second load and logs a CORS error that looks like a
         broken page ("Cross-origin script load denied"). Same-src check rather
         than a same-origin check, so a section's own script still re-runs when
         it is genuinely new to the document. */
      if (original.src) {
        var already = document.querySelector('script[src="' + original.getAttribute('src') + '"]');
        if (already && !root.contains(already)) return;
      }
      var replacement = document.createElement('script');
      Array.prototype.forEach.call(original.attributes, function (attr) {
        replacement.setAttribute(attr.name, attr.value);
      });
      if (!original.src) replacement.textContent = original.textContent;
      original.parentNode.replaceChild(replacement, original);
    });
  }

  function applyDocument(html, url) {
    var main = document.getElementById(MAIN_ID);
    if (!main) return 'no #MainContent to swap into';

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var incoming = parsed.getElementById(MAIN_ID);
    if (!incoming) return 'the response had no #MainContent in it';

    /* TEMPLATE-GATED STYLESHEETS HAVE TO COME WITH THE MARKUP (Devin, 10 Aug:
       "the CSS for a product page doesn't load and i need to refresh").

       snippets/custom.globals.liquid USED TO load custom.pdp.css and
       custom.pdp.buybar.css only when `request.page_type == 'product'`, so a
       product swapped into a document that started elsewhere arrived with an
       unstyled buy bar. The cause was removed on 10 Aug — the theme's own
       assets now load identically on every template — and this block stays as
       the net for head content the theme does not control: an app installed
       later can inject a per-template stylesheet through content_for_header,
       and it would fail in exactly the way the buy bar did.

       Matched on href so a stylesheet is never added twice, and appended
       before the markup so the browser starts fetching while it parses. */
    parsed.querySelectorAll('link[rel="stylesheet"][href]').forEach(function (link) {
      var href = link.getAttribute('href');
      if (document.querySelector('link[rel="stylesheet"][href="' + href + '"]')) return;
      var copy = document.createElement('link');
      copy.rel = 'stylesheet';
      copy.href = href;
      document.head.appendChild(copy);
    });

    /* THE SAME NET, ON SCRIPTS. scripts.liquid used to load
       sticky-add-to-cart.js only on the product template, so a swapped-in PDP
       had the markup and no sticky bar; ungated 10 Aug along with the
       stylesheets above. Kept for the same reason: an app's per-template head
       script would otherwise be forgotten by a swap. Same href-matching, so
       nothing runs twice. */
    parsed.querySelectorAll('head script[src]').forEach(function (node) {
      var src = node.getAttribute('src');
      if (document.querySelector('script[src="' + src + '"]')) return;
      var copy = document.createElement('script');
      Array.prototype.forEach.call(node.attributes, function (attr) {
        copy.setAttribute(attr.name, attr.value);
      });
      document.head.appendChild(copy);
    });

    /* CANONICAL AND THE META TAGS DESCRIBE THE PAGE, and they were still
       describing the one the visit started on — a shopper sharing a product
       page would have posted a link to the homepage, and analytics would have
       recorded the wrong URL. Cheap to fix and invisible until it matters,
       which is the worst kind of bug to leave in.

       WHOLESALE, NOT A LIST. The first version updated seven hand-picked
       selectors, and the 10 Aug audit found everything outside the list stale
       after a swap: twitter:title and twitter:description on every template,
       og:image:secure_url / width / height and both og:price tags on a
       product. It also missed the OTHER direction — the cart renders no meta
       description at all, so the description of whatever page came before it
       survived. Snippets/meta-tags.liquid decides what a page says about
       itself; mirroring its output beats maintaining a parallel list of it
       here. These are inert head tags — no fetch, no paint — so replace them
       all: everything the old page claimed goes, everything the new page
       claims arrives, absences included. */
    var META = 'link[rel="canonical"], meta[name="description"], ' +
      'meta[property^="og:"], meta[name^="twitter:"], meta[property^="twitter:"]';
    document.head.querySelectorAll(META).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    parsed.querySelectorAll(META).forEach(function (el) {
      document.head.appendChild(el.cloneNode(true));
    });

    main.innerHTML = incoming.innerHTML;

    /* data-template drives real CSS — the green collection title tag is
       #MainContent[data-template="collection"] — so it has to travel with the
       markup or a product page would be styled as whatever came before it. */
    main.dataset.template = incoming.dataset.template || '';
    if (incoming.className) main.className = incoming.className;

    /* Theme.template.name is the same `{{ template }}` value, stamped by an
       inline head script that a swap cannot re-run — measured stale after a
       swap ('index' on a product page). No theme script reads it TODAY (10 Aug
       audit, grepped), but it is Horizon's own API and the first update that
       consults it would get the page the visit started on. `Theme` is a
       top-level const — a lexical global, not window.Theme — hence typeof. */
    try {
      if (typeof Theme !== 'undefined' && Theme.template) {
        Theme.template.name = main.dataset.template;
      }
    } catch (e) {
      /* nothing of ours depends on it */
    }

    /* TITLE BEFORE SCRIPTS. Sections can set their own document.title —
       custom-filter-title.liquid does, to keep an internal tag out of the tab —
       and assigning the fetched <title> afterwards would stamp over it. */
    var title = parsed.querySelector('title');
    if (title) document.title = title.textContent;

    /* THE SEARCH PILL BELONGS TO THE PAGE, NOT THE SESSION (Devin, 10 Aug:
       "search for cock. click on logo in header. cock stays in the search
       bar"). Liquid fills that box from the page being rendered — the search
       term, or the tag a redirected search landed on — and a full load used to
       reset it for free. The header no longer reloads, so the word from two
       pages ago sits there implying it has something to do with what is on
       screen.

       The fetched document already contains the header Liquid WOULD have
       rendered, so the correct value is sitting in `parsed` — no rule about
       which templates should clear it and which should not, and it stays right
       if that Liquid ever changes.

       Not while it has focus: someone typing when a swap lands keeps what they
       typed. */
    /* The pill lives in the MASTHEAD (sections/custom-header.liquid), which is
       its own section outside #header-component — scoping to the header
       component matched nothing and the value never moved. */
    var pill = document.querySelector('input.ffg-masthead__search-input');
    var incomingPill = parsed.querySelector('input.ffg-masthead__search-input');
    if (pill && incomingPill && document.activeElement !== pill) {
      pill.value = incomingPill.getAttribute('value') || '';

      /* THE WIDTH IS PAGE STATE TOO (10 Aug, Devin: "when you click on a
         soap from a search result... the search pill should shrink back
         down"). Liquid puts is-wide on the form only on search/tag pages;
         mirroring the fetched form's class lets the pill breathe back in on
         the way to a product — the existing width transition animates the
         shrink for free. Inside the same focus guard as the value: a pill
         being typed in is not ours to resize. */
      var liveForm = pill.closest('.ffg-masthead__search');
      var incomingForm = incomingPill.closest('.ffg-masthead__search');
      if (liveForm && incomingForm) {
        liveForm.classList.toggle('is-wide', incomingForm.classList.contains('is-wide'));
      }
    }

    activateScripts(main);
    return null;
  }

  /* Shopify's analytics counts a page view on page load, which now happens
     once per visit instead of once per page. This is the documented-enough
     nudge; wrapped because it is not a contract and a missing library must
     never break navigation. */
  function countPageView(url) {
    try {
      var lib = window.ShopifyAnalytics && window.ShopifyAnalytics.lib;
      if (lib && typeof lib.page === 'function') lib.page(null, { path: url });
    } catch (e) {
      /* analytics is never worth an exception */
    }
  }

  /* A screen reader is told nothing by innerHTML changing. Moving focus to the
     top of the new content is the same thing a real navigation does. */
  /* THIS WAS THE PHANTOM 1px OUTLINE (Devin, 10 Aug, three sightings: "a
     thin 1px black outline on product shots... it seems to happen once
     toward the beginning of a session to one element"). Two faults stacked:
     `h1, [tabindex="-1"]` returns the DOM-ORDER first of EITHER, and
     Horizon's slideshow slides carry tabindex="-1" — so on any page where a
     carousel precedes the heading, this focused a PRODUCT SLIDE. And WebKit
     decides programmatic focus is :focus-visible, painting its 1px black UA
     ring around whatever was focused — one element, once per swap, gone at
     the next tap, never on a hard load. Measured: activeElement computed
     `outline: 1px solid rgb(0,0,0)` after a chip-click swap.

     Headings now win outright, and custom.css.liquid removes the ring from
     script-focus targets inside #MainContent — nothing keyboard-reachable
     loses its ring, because tabindex="-1" cannot be tabbed to. */
  function refocus() {
    var main = document.getElementById(MAIN_ID);
    if (!main) return;
    var target = main.querySelector('h1') || main.querySelector('[tabindex="-1"]') || main;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
  }

  function rememberScroll() {
    scrollByPath[location.pathname] = scrollPosition();
    try {
      history.replaceState(
        Object.assign({}, history.state || {}, { ffgScroll: scrollPosition() }),
        '',
        location.href
      );
    } catch (e) {
      /* a cross-origin or opaque state; the scroll is not worth failing over */
    }
  }

  /* /collections/all IS ALWAYS BEST SELLING (Devin, 4 Aug, and again on 10 Aug:
     "when i click on all 207 flavors i want to arrive at a collection sorted by
     best selling"). That rule lives in a <head> script — and a head script does
     not run on a swap, because there is no new document. So every link to the
     all-products listing was landing title-ascending as soon as navigation
     stopped reloading the page.

     Applied to the URL before the fetch rather than after arrival: one request,
     and the address bar never shows the unsorted URL. A shopper who has chosen
     a sort has sort_by set already, which makes this a no-op. */
  function withDefaultSort(url) {
    try {
      var u = new URL(url, location.origin);
      if (u.pathname.replace(/\/$/, '') === '/collections/all' && !u.searchParams.has('sort_by')) {
        u.searchParams.set('sort_by', 'best-selling');
        return u.toString();
      }
    } catch (e) {
      /* a URL we cannot parse is a URL we should not rewrite */
    }
    return url;
  }

  function go(url, options) {
    var opts = options || {};
    url = withDefaultSort(url);
    if (!canSwap()) {
      window.location.href = url;
      return;
    }

    if (inFlight) inFlight.abort();
    var controller = new AbortController();
    inFlight = controller;
    swapping = true;

    document.documentElement.classList.add('ffg-swapping');

    fetch(url, { signal: controller.signal, headers: { 'X-Requested-With': 'ffg-pageswap' } })
      .then(function (response) {
        if (!response.ok) throw new Error('responded ' + response.status);
        var type = response.headers.get('content-type') || '';
        /* A redirect to checkout, a file, a JSON endpoint: not our business. */
        if (type.indexOf('text/html') === -1) throw new Error('not html');
        if (response.redirected && NEVER.test(new URL(response.url).pathname)) {
          throw new Error('redirected somewhere we do not own');
        }
        return response.text();
      })
      .then(function (html) {
        /* SUPERSEDED-REQUEST GUARD (15 Aug audit fix). A rapid second go()
           aborts this one and reassigns inFlight, but abort() does not always
           reject before the body resolves (the response can already be
           buffered). Applying this now-stale document would stomp the newer
           navigation — bail if we are no longer the current request. */
        if (inFlight !== controller) return;
        var problem = applyDocument(html, url);
        if (problem) throw new Error(problem);

        if (opts.push !== false) {
          rememberScroll();
          history.pushState({ ffg: true, ffgScroll: 0 }, '', url);
        }
        currentPath = new URL(url, location.origin).pathname;
        var to = opts.scroll;
        if (to == null && opts.push === false) to = scrollByPath[location.pathname];
        scrollToY(to || 0);
        /* The drawer's closeDialog restores ITS OLD scroll position after its
           close animation — on a navigation that's the WRONG page's position.
           Re-assert ours after the animation window. */
        if (opts.overlayClosed) {
          window.setTimeout(function () {
            scrollToY(to || 0);
          }, 450);
        }
        refocus();
        countPageView(url);
        document.dispatchEvent(new CustomEvent('ffg:navigated', { detail: { url: url } }));
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        /* A superseded request that errored must NOT yank the page to its own
           URL — the newer navigation owns the address bar now. */
        if (inFlight !== controller) return;
        window.location.href = url;
      })
      .finally(function () {
        /* Only the current request clears the shared swap state. An aborted
           predecessor's finally used to drop the class + flag while the newer
           request was still loading (the race the audit flagged) — guard it. */
        if (inFlight !== controller) return;
        inFlight = null;
        swapping = false;
        document.documentElement.classList.remove('ffg-swapping');
      });
  }

  /* Kept current as the shopper scrolls, not only at click time: on a popstate
     the URL has already moved by the time we hear about it, so there is no
     later moment at which the page being left can still be identified. */
  /* CAPTURE, because a scroll event on an element does not bubble to the
     window — and the scrolling element here is .page-wrapper, not the page. */
  window.addEventListener(
    'scroll',
    function () {
      if (!swapping) scrollByPath[location.pathname] = scrollPosition();
    },
    { passive: true, capture: true }
  );

  /* NOTHING APPEARS TO HAPPEN OTHERWISE (Devin, 10 Aug: "you click on it and
     because the page loads behind the menu nothing seems to happen"). A real
     navigation used to close the drawer by destroying the document; now that
     the document survives, the drawer survives with it and hides the page that
     just changed underneath it.

     `open` is removed rather than the summary being clicked, because clicking
     it would toggle — a second tap on an already-closed drawer would reopen it.
     The cart drawer and any dialog get the same treatment: whatever is covering
     the page when the shopper asks for a new page should not still be there
     when it arrives. */
  function closeOverlays() {
    /* EVERY OPEN details IN THE DRAWER, ANY DEPTH (13 Aug, Devin, iPad:
       navigate from Products submenu, reopen the hamburger, "the only thing
       it says is products, the other links are missing"). The old selector
       reset the container and the second level but NOT the drawer's
       `menu-drawer--has-submenu-opened` class or the third-level accordion
       details — the drawer reopened INTO the dead submenu panel, whose
       links sit in their pre-animation state, so all that showed was the
       back-button row reading "Products". Horizon's own cleanup cannot be
       leaned on here: header-drawer.js defers its reset to an animationend
       that a mid-swap close never fires. Reset everything, synchronously. */
    document.querySelectorAll('header-drawer details[open], details.menu-drawer-container[open], details[open].menu-drawer__menu-container').forEach(function (el) {
      el.removeAttribute('open');

      /* THE ICON IS NOT DRIVEN BY [open] ALONE (Devin, 10 Aug: "when the tray
         closes it still shows the x icon in the corner"). Horizon tracks the
         drawer with a `menu-open` class on the container and aria-expanded on
         the summary, and swaps the hamburger for the X off those. Dropping only
         the attribute closed the tray and left it wearing a close button for a
         drawer that was no longer there. */
      el.classList.remove('menu-open');
      var summary = el.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('.menu-drawer--has-submenu-opened').forEach(function (el) {
      el.classList.remove('menu-drawer--has-submenu-opened');
    });
    /* THROUGH THE COMPONENT, NOT THE ELEMENT (19 Aug — Devin's empty-cart
       bug: close the tray via "keep shopping" and the page is dead until a
       refresh on iPhone; first click lost on desktop). Horizon's scroll
       lock is a `scroll-lock` attribute on <html> guarded by an OWNER
       REGISTRY with a re-arming MutationObserver — raw dialog.close()
       never empties the registry, so the lock survives the navigation and
       the new page arrives unscrollable, pinned to the top, eating taps.
       Only the component's closeDialog() calls unlockScroll. Returns
       whether anything was open so go() can out-wait the component's own
       delayed scroll-restore. */
    var ffg_closed_any = false;
    document.querySelectorAll('dialog[open]').forEach(function (el) {
      ffg_closed_any = true;
      /* theme-drawer FIRST: the cart tray's host is <theme-drawer
         id="cart-drawer">, not a dialog-component — the 19 Aug freeze fix
         v1 missed it (hostFound null → raw close → lock stuck; probed). */
      var host = el.closest('theme-drawer, dialog-component, cart-drawer-component');
      try {
        if (host && typeof host.closeDialog === 'function') {
          host.closeDialog();
        } else if (host && typeof host.close === 'function') {
          host.close();
        } else {
          el.close();
        }
      } catch (e) {
        try { el.close(); } catch (e2) { el.removeAttribute('open'); }
      }
    });
    return ffg_closed_any;
  }

  document.addEventListener('click', function (event) {
    if (!canSwap()) return;
    var link = event.target.closest && event.target.closest('a[href]');
    if (!isSwappable(link, event)) return;
    /* THE PAGE YOU ARE ALREADY ON still has to answer the tap. Nothing to
       fetch, but the shopper asked to go somewhere and the drawer is covering
       the page they would arrive at — so it closes, which is the whole visible
       response (Devin, 10 Aug: "when you click the link for the page you're
       already on nothing happens. it should close the tray"). */
    if (link.href === location.href) {
      event.preventDefault();
      closeOverlays();
      return;
    }
    event.preventDefault();
    var ffg_had_overlay = closeOverlays();
    go(link.href, { overlayClosed: ffg_had_overlay });
  });

  window.addEventListener('popstate', function (event) {
    if (!canSwap()) return;
    var state = event.state || {};
    var ours = state.ffg || state.ffgSearch;

    /* THE FLAG IS NOT ENOUGH, and Back proved it. Horizon's results-list pushes
       its own history entries as you page and sort a collection — no flag of
       ours on them — so Back from a product landed on `/collections/new?page=1`
       with the product still on screen: the URL moved and the markup did not.
       Comparing the PATH to what is rendered catches those entries too.

       Query-only changes are deliberately left alone: a sort or a page number
       belongs to the component that pushed it, and swapping the whole of
       #MainContent underneath it would throw away its state. */
    if (!ours && location.pathname === currentPath) return;

    go(location.href, { push: false, scroll: state.ffgScroll });
  });

  /* The browser's own guess is wrong once the markup is swapped under it. */
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  window.FFGPageSwap = { go: go, applyDocument: applyDocument, isSwappable: isSwappable, scrollToY: scrollToY, scrollPosition: scrollPosition };
})();
